import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import ReadingsHelpers from 'utils/ReadingsHelpers';
import { useSettings } from 'hooks';

import ReadingGraph from './ReadingGraph';
import { Box, Grid, useMediaQuery } from '@material-ui/core';
import { KeySpinner, Swipable } from 'components';
import { GraphLegendMobile, GraphLegendDesktop } from '.';

const useStyles = makeStyles(() => ({
  chartTitle: {
    fontWeight: 300,
  },
  beta: {
    color: '#1d69ff',
    fontWeight: 450,
  },
}));

function ReadingGraphs({ dataSet, readingTypes, graphDomain }) {
  const { t } = useTranslation();
  const mobileView = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const classes = useStyles();
  const [measurement, setMeasurement] = useState('SOC');
  const [lookup, setLookup] = useState(['SOC']);
  const { convertPPM, convertUnits } = useSettings();
  let i = 0;
  const graphs = [];
  let mobileConversion = undefined;
  const [mobileStatTab, setMobileStatTab] = useState(0);

  const availableReadingTypes = [];
  _.forEach(readingTypes, (reading) => {
    const lookups = [];
    const measurement = reading.replace(/_\d$/, '');
    if (!_.find(availableReadingTypes, { measurement: measurement })) {
      _.forEach(readingTypes, (lookup) => {
        const match = lookup.replace(/_\d$/, '');
        if (measurement === match) {
          lookups.push(lookup);
        }
      });
      availableReadingTypes.push({ lookups: lookups, measurement: measurement });
    }
  });

  const getConvert = (name) => {
    let NPK = ['N', 'P', 'K'];
    if (NPK.includes(name?.toUpperCase())) {
      return convertUnits;
    } else if (name?.includes('temp')) {
      return convertUnits;
    } else if (name?.includes('ppm')) {
      return convertPPM;
    }
    return true;
  };

  const changeMeasurement = (tab) => {
    setMobileStatTab(tab);
    const measurement = availableReadingTypes[tab].measurement;
    const reading = [];
    _.forEach(readingTypes, (type) => {
      const trimmedMeasurement = type.replace(/_\d$/, '');
      if (trimmedMeasurement === measurement) {
        reading.push(type);
      }
    });

    setMeasurement(measurement);
    setLookup(reading);
  };

  if (!mobileView) {
    _.forEach(availableReadingTypes, (read) => {
      const readingName = read.measurement;
      const convert = getConvert(read.measurement);
      graphs.push(
        <Grid container key={readingName + i++} alignItems="flex-start" spacing={1}>
          <Grid container item>
            {readingName == 'p' ? (
              <Grid item>
                <Typography display="inline" variant="h5" className={classes.chartTitle}>
                  {`${t(readingName)} `}
                </Typography>
                <Typography display="inline" variant="h5" className={classes.beta}>
                  {`${t('beta')} `}
                </Typography>
                <Typography display="inline" variant="h5" className={classes.chartTitle}>
                  {`${ReadingsHelpers.getUnits(readingName, '', convert)}`}
                </Typography>
              </Grid>
            ) : (
              <Typography variant="h5" className={classes.chartTitle}>
                {`${t(readingName)} ${ReadingsHelpers.getUnits(readingName, '', convert)}`}
              </Typography>
            )}
          </Grid>
          <React.Suspense fallback={<CircularProgress />}>
            <ReadingGraph
              mobileView={mobileView}
              useSuspense={false}
              dataSet={dataSet}
              title={t(readingName)}
              readingType={read.lookups}
              height={300}
              syncId="nitrogen"
              convert={convert}
              graphDomain={graphDomain}
            />
          </React.Suspense>
        </Grid>
      );
    });
  }

  return mobileView ? (
    <Swipable dataKeys={availableReadingTypes} onTabChange={(tab) => changeMeasurement(tab)} tabValue={mobileStatTab}>
      <GraphLegendMobile />
      <Grid container item>
        <Grid item xs={9}>
          <Typography variant="h5" className={classes.chartTitle}>{`${t(measurement)} ${ReadingsHelpers.getUnits(
            measurement,
            '',
            getConvert(measurement)
          )}`}</Typography>
          <ReadingGraph
            mobileView={mobileView}
            useSuspense={false}
            dataSet={dataSet}
            title={t(measurement)}
            readingType={lookup}
            height={300}
            syncId="nitrogen"
            convert={mobileConversion}
            graphDomain={graphDomain}
          />
        </Grid>
        <Grid item xs={3}>
          <KeySpinner
            tab={mobileStatTab}
            changeMeasurement={changeMeasurement}
            availableReadingTypes={availableReadingTypes}
          />
        </Grid>
      </Grid>
    </Swipable>
  ) : (
    <Box>
      <GraphLegendDesktop />
      <Box>{graphs}</Box>
    </Box>
  );
}

ReadingGraphs.propTypes = {
  dataSet: PropTypes.array.isRequired,
  readingTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  graphDomain: PropTypes.object,
};

ReadingGraphs.defaultProps = {
  readingTypes: [],
  dataSet: [],
  graphDomain: { start: 0, end: 0 },
};

export default ReadingGraphs;
